import { action, observable, runInAction } from 'mobx';
import { ErrorInterface } from 'modules/core/api/globalApi';
import controller from './controller';
import {
  ContractListItem,
  CreateContractInterface,
  UpdateContractInterface,
} from './partnerContractApi';

export interface ContractStore {
  // ==== Полный CRUD-список договоров (используется PartnerContractsPanel,
  //      PartnerContractModal, PartnerFeeEditModal, PartnerFeeLegalEditModal,
  //      PartnerLegalModal — для блокировки чекбокса "Проверено").
  // contractListByPartnerId / contractListByLegalId фиксируют, по какому
  // источнику последний раз грузился список (для инвалидации/диагностики).
  contractList: ContractListItem[],
  contractListByPartnerId: string | null | undefined,
  contractListByLegalId: string | null | undefined,
  currentContract: ContractListItem | null,
  loading: boolean,
  error: ErrorInterface | null,

  // ==== Изолированный список для ContractSelector (живёт отдельно, чтобы
  //      переключение партнёра/юр.лица в селекторе не дёргало панель CRUD).
  contractListForSelector: ContractListItem[],
  contractListForSelectorLoading: boolean,
  partnerLegalIdForSelector?: string,
  selectorError: ErrorInterface | null,
}

export const contractStore = observable({
  contractList: [],
  contractListByPartnerId: undefined,
  contractListByLegalId: undefined,
  currentContract: null,
  loading: false,
  error: null,

  contractListForSelector: [],
  contractListForSelectorLoading: false,
  partnerLegalIdForSelector: undefined,
  selectorError: null,
} as ContractStore);

const buildErrorFromResponse = (response: any): ErrorInterface => {
  const { errorData } = response;
  return {
    message: errorData?.message,
    details: errorData?.details,
    errorCode: errorData?.errorCode,
    timestamp: errorData?.timestamp,
    status: response?.status,
    connectError: response?.connectError,
    isError: response?.error,
  };
};

export const contractActions = {

  handleError: action((response) => {
    if (response.errorData) {
      contractStore.error = buildErrorFromResponse(response);
      throw response.errorData;
    }
  }),

  clearError: action(() => {
    contractStore.error = null;
  }),

  setCurrentContract: action((item: ContractListItem | null) => {
    contractStore.currentContract = item;
  }),

  clearCurrentContract: action(() => {
    contractStore.currentContract = null;
  }),

  // ==== Загрузка основного списка ====

  getContractListByPartnerId: async (partnerId: string): Promise<ContractListItem[]> => {
    runInAction(() => {
      contractStore.loading = true;
      contractStore.contractListByPartnerId = partnerId;
      contractStore.contractListByLegalId = null;
      contractStore.error = null;
    });
    const response = await controller.getPartnerContractList(partnerId);
    runInAction(() => {
      if (response.errorData) {
        contractStore.error = buildErrorFromResponse(response);
        contractStore.contractList = [];
      } else {
        contractStore.contractList = response.data ?? [];
      }
      contractStore.loading = false;
    });
    return response.errorData ? [] : (response.data ?? []);
  },

  getContractListByLegalId: async (partnerLegalId: string): Promise<ContractListItem[]> => {
    runInAction(() => {
      contractStore.loading = true;
      contractStore.contractListByLegalId = partnerLegalId;
      contractStore.contractListByPartnerId = null;
      contractStore.error = null;
    });
    const response = await controller.getLegalContractList(partnerLegalId);
    runInAction(() => {
      if (response.errorData) {
        contractStore.error = buildErrorFromResponse(response);
        contractStore.contractList = [];
      } else {
        contractStore.contractList = response.data ?? [];
      }
      contractStore.loading = false;
    });
    return response.errorData ? [] : (response.data ?? []);
  },

  clearContractList: action(() => {
    contractStore.contractList = [];
    contractStore.contractListByPartnerId = undefined;
    contractStore.contractListByLegalId = undefined;
    contractStore.error = null;
  }),

  // ==== CRUD ====

  createContract: async (request: CreateContractInterface): Promise<ContractListItem> => {
    contractActions.clearError();
    const response = await controller.createContract(request);
    contractActions.handleError(response);
    runInAction(() => {
      contractStore.contractList = [...contractStore.contractList, response.data];
      contractStore.currentContract = response.data;
    });
    return response.data;
  },

  updateContract: async (request: UpdateContractInterface): Promise<ContractListItem> => {
    contractActions.clearError();
    const response = await controller.updateContract(request);
    contractActions.handleError(response);
    runInAction(() => {
      contractStore.contractList = contractStore.contractList
        .map((v) => (v.id === request.id ? response.data : v));
      if (contractStore.currentContract?.id === request.id) {
        contractStore.currentContract = response.data;
      }
    });
    return response.data;
  },

  deleteContract: async (id: string): Promise<void> => {
    contractActions.clearError();
    const response = await controller.deleteContract(id);
    contractActions.handleError(response);
    runInAction(() => {
      contractStore.contractList = contractStore.contractList.filter((v) => v.id !== id);
      if (contractStore.currentContract?.id === id) {
        contractStore.currentContract = null;
      }
    });
  },

  // ==== Изолированный список для ContractSelector ====

  getContractListForSelectorByPartnerLegalId: async (partnerLegalId: string) => {
    runInAction(() => {
      contractStore.contractListForSelectorLoading = true;
      contractStore.selectorError = null;
    });
    const response = await controller.getLegalContractList(partnerLegalId);
    runInAction(() => {
      if (response.errorData) {
        contractStore.selectorError = buildErrorFromResponse(response);
        contractStore.contractListForSelector = [];
      } else {
        contractStore.contractListForSelector = response.data ?? [];
        contractStore.partnerLegalIdForSelector = partnerLegalId;
      }
      contractStore.contractListForSelectorLoading = false;
    });
  },

  getContractListForSelectorByPartnerId: async (partnerId: string) => {
    runInAction(() => {
      contractStore.contractListForSelectorLoading = true;
      contractStore.selectorError = null;
    });
    const response = await controller.getPartnerContractList(partnerId);
    runInAction(() => {
      if (response.errorData) {
        contractStore.selectorError = buildErrorFromResponse(response);
        contractStore.contractListForSelector = [];
      } else {
        contractStore.contractListForSelector = response.data ?? [];
        contractStore.partnerLegalIdForSelector = partnerId;
      }
      contractStore.contractListForSelectorLoading = false;
    });
  },

  clearContractListForSelector: action(() => {
    contractStore.contractListForSelector = [];
    contractStore.partnerLegalIdForSelector = undefined;
  }),

};
